import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.exura.components.layouts.NavigableLayout as _com_exura_components_layouts_NavigableLayout
import com.exura.components.layouts.NotLoggedInLayout as _com_exura_components_layouts_NotLoggedInLayout
import com.exura.components.layouts.plan.PlanConstructorLayout as _com_exura_components_layouts_plan_PlanConstructorLayout
import com.exura.pages.Index as _com_exura_pages_Index
import com.exura.pages.`import`.ImportPlansPage as _com_exura_pages_import_ImportPlansPage
import com.exura.pages.dashboard.DashboardPage as _com_exura_pages_dashboard_DashboardPage
import com.exura.pages.login.LoginPage as _com_exura_pages_login_LoginPage
import com.exura.pages.planConstructor.PlanConstructorIndexPage as _com_exura_pages_planConstructor_PlanConstructorIndexPage
import com.exura.pages.planCreator.PlanCreatorPage as _com_exura_pages_planCreator_PlanCreatorPage
import com.exura.pages.planDistribution.PlanDistributionPage as _com_exura_pages_planDistribution_PlanDistributionPage
import com.exura.pages.planPreview.CollectionPreviewIndexPage as _com_exura_pages_planPreview_CollectionPreviewIndexPage
import com.exura.pages.planPreview.NestedSeriesPreviewIndexPage as _com_exura_pages_planPreview_NestedSeriesPreviewIndexPage
import com.exura.pages.planPreview.PlanPreviewIndexPage as _com_exura_pages_planPreview_PlanPreviewIndexPage
import com.exura.pages.planPreview.SeriesPreviewIndexPage as _com_exura_pages_planPreview_SeriesPreviewIndexPage
import com.exura.pages.publicplans.PublicPlanDetailsPage as _com_exura_pages_publicplans_PublicPlanDetailsPage
import com.exura.pages.publicplans.PublicPlanListPage as _com_exura_pages_publicplans_PublicPlanListPage
import com.exura.pages.register.RegisterPage as _com_exura_pages_register_RegisterPage
import com.exura.pages.settings.ProviderProfileSettingsPage as _com_exura_pages_settings_ProviderProfileSettingsPage
import com.exura.pages.test.MyPage as _com_exura_pages_test_MyPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Exura"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx, pageMethod -> 
            _com_exura_components_layouts_NavigableLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.registerLayout(
            "com.exura.components.layouts.NotLoggedInLayout",
        ) { pageCtx, pageMethod -> 
            _com_exura_components_layouts_NotLoggedInLayout(pageCtx) {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "com.exura.components.layouts.plan.PlanConstructorLayout",
            parentLayoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx, pageMethod -> 
            currentLayoutScope<com.exura.components.layouts.NavigableLayoutScope>().apply {
                _com_exura_components_layouts_plan_PlanConstructorLayout(pageCtx) {
                    provideLayoutScope(this) {
                        pageMethod(pageCtx)
                    }
                }
            }
        }
        ctx.router.register(
            "/",
        ) { pageCtx -> 
            _com_exura_pages_Index()
        }
        ctx.router.register(
            "/dashboard",
            layoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.NavigableLayoutScope>().apply {
                _com_exura_pages_dashboard_DashboardPage()
            }
        }
        ctx.router.register(
            "/import-plans",
            layoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.NavigableLayoutScope>().apply {
                _com_exura_pages_import_ImportPlansPage()
            }
        }
        ctx.router.register(
            "/login",
            layoutId = "com.exura.components.layouts.NotLoggedInLayout",
        ) { pageCtx -> 
            _com_exura_pages_login_LoginPage()
        }
        ctx.router.register(
            "/plan-creator",
            layoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx -> 
            _com_exura_pages_planCreator_PlanCreatorPage()
        }
        ctx.router.register(
            "/plan-distribution/{planId}",
            layoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.NavigableLayoutScope>().apply {
                _com_exura_pages_planDistribution_PlanDistributionPage()
            }
        }
        ctx.router.register(
            "/plan-preview/{planId}",
            layoutId = "com.exura.components.layouts.plan.PlanConstructorLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.plan.PlanConstructorLayoutScope>().apply {
                _com_exura_pages_planPreview_PlanPreviewIndexPage()
            }
        }
        ctx.router.register(
            "/plan-preview/{planId}/collection/{collectionId}",
            layoutId = "com.exura.components.layouts.plan.PlanConstructorLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.plan.PlanConstructorLayoutScope>().apply {
                _com_exura_pages_planPreview_CollectionPreviewIndexPage()
            }
        }
        ctx.router.register(
            "/plan-preview/{planId}/collection/{collectionId}/series/{seriesId}",
            layoutId = "com.exura.components.layouts.plan.PlanConstructorLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.plan.PlanConstructorLayoutScope>().apply {
                _com_exura_pages_planPreview_NestedSeriesPreviewIndexPage()
            }
        }
        ctx.router.register(
            "/plan-preview/{planId}/series/{seriesId}",
            layoutId = "com.exura.components.layouts.plan.PlanConstructorLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.plan.PlanConstructorLayoutScope>().apply {
                _com_exura_pages_planPreview_SeriesPreviewIndexPage()
            }
        }
        ctx.router.register(
            "/planConstructor",
            layoutId = "com.exura.components.layouts.plan.PlanConstructorLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.plan.PlanConstructorLayoutScope>().apply {
                _com_exura_pages_planConstructor_PlanConstructorIndexPage()
            }
        }
        ctx.router.register(
            "/publicplan",
            layoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.NavigableLayoutScope>().apply {
                _com_exura_pages_publicplans_PublicPlanListPage()
            }
        }
        ctx.router.register(
            "/publicplan/{publicPlanEntryId}",
            layoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.NavigableLayoutScope>().apply {
                _com_exura_pages_publicplans_PublicPlanDetailsPage()
            }
        }
        ctx.router.register(
            "/register",
            layoutId = "com.exura.components.layouts.NotLoggedInLayout",
        ) { pageCtx -> 
            _com_exura_pages_register_RegisterPage()
        }
        ctx.router.register(
            "/settings",
            layoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.NavigableLayoutScope>().apply {
                _com_exura_pages_settings_ProviderProfileSettingsPage()
            }
        }
        ctx.router.register(
            "/test",
            layoutId = "com.exura.components.layouts.NavigableLayout",
        ) { pageCtx -> 
            currentLayoutScope<com.exura.components.layouts.NavigableLayoutScope>().apply {
                _com_exura_pages_test_MyPage()
            }
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("app-button", com.exura.components.buttons.AppButtonStyle)
        ctx.theme.registerStyle("dialog-overlay", com.exura.components.dialogs.DialogOverlayStyle)
        ctx.theme.registerStyle("dialog-panel", com.exura.components.dialogs.DialogPanelStyle)
        ctx.theme.registerStyle("image-selection-ribbon", com.exura.components.dialogs.imageSelection.ImageSelectionRibbonStyle)
        ctx.theme.registerStyle("inline-input", com.exura.components.inputs.InlineInputStyle)
        ctx.theme.registerStyle("mode-toggle-group", com.exura.components.inputs.ModeToggleGroupStyle)
        ctx.theme.registerStyle("mode-toggle-option", com.exura.components.inputs.ModeToggleOptionStyle)
        ctx.theme.registerStyle("nav-text", com.exura.components.layouts.nav.NavTextStyle)
        ctx.theme.registerStyle("nav-navigable-element", com.exura.components.layouts.nav.NavNavigableElementStyle)
        ctx.theme.registerStyle("nav-section-header", com.exura.components.layouts.nav.NavSectionHeaderStyle)
        ctx.theme.registerStyle("nav", com.exura.components.layouts.nav.NavStyle)
        ctx.theme.registerStyle("plan-constructor-header", com.exura.components.layouts.plan.PlanConstructorHeaderStyle)
        ctx.theme.registerStyle("plan-constructor-header-element", com.exura.components.layouts.plan.PlanConstructorHeaderElementStyle)
        ctx.theme.registerStyle("side-bar", com.exura.components.layouts.plan.SideBarStyle)
        ctx.theme.registerStyle("side-bar-row", com.exura.components.layouts.plan.SideBarRowStyle)
        ctx.theme.registerStyle("side-bar-row-content", com.exura.components.layouts.plan.SideBarRowContentStyle)
        ctx.theme.registerStyle("gant-day-box", com.exura.components.layouts.plan.GantDayBoxStyle)
        ctx.theme.registerStyle("plan-constructor-side-bar-day-box", com.exura.components.layouts.plan.PlanConstructorSideBarDayBoxStyle)
        ctx.theme.registerStyle("day-box-series-element", com.exura.components.layouts.plan.DayBoxSeriesElementStyle)
        ctx.theme.registerStyle("spawned-element-tooltip", com.exura.components.layouts.plan.SpawnedElementTooltipStyle)
        ctx.theme.registerStyle("constructor-series-icon", com.exura.components.layouts.plan.constructorSeriesIconStyle)
        ctx.theme.registerStyle("loader-overlay", com.exura.components.loading.LoaderOverlayStyle)
        ctx.theme.registerStyle("loader", com.exura.components.loading.LoaderStyle)
        ctx.theme.registerStyle("card-entry", com.exura.components.pageTypography.CardEntryStyle)
        ctx.theme.registerStyle("list-entry", com.exura.components.pageTypography.ListEntryStyle)
        ctx.theme.registerStyle("list-entry-image", com.exura.components.pageTypography.ListEntryImageStyle)
        ctx.theme.registerStyle("step-number", com.exura.components.pageTypography.stepNumberStyle)
        ctx.theme.registerStyle("page-error-content", com.exura.components.pageTypography.PageErrorContentStyle)
        ctx.theme.registerStyle("page-content", com.exura.components.pageTypography.PageContentStyle)
        ctx.theme.registerStyle("page-navigation-header", com.exura.components.pageTypography.PageNavigationHeaderStyle)
        ctx.theme.registerStyle("page-navigation-breadcrumbs", com.exura.components.pageTypography.PageNavigationBreadcrumbsStyle)
        ctx.theme.registerStyle("options-section", com.exura.components.pageTypography.OptionsSectionStyle)
        ctx.theme.registerStyle("secondary-page-content", com.exura.components.pageTypography.SecondaryPageContentStyle)
        ctx.theme.registerStyle("exura-text", com.exura.components.pageTypography.ExuraTextStyle)
        ctx.theme.registerStyle("snackbar", com.exura.components.snackbar.SnackbarStyle)
        ctx.theme.registerVariant("-round", com.exura.components.buttons.ButtonRoundVariant
        )
        ctx.theme.registerVariant("-primary", com.exura.components.buttons.ButtonPrimaryVariant
        )
        ctx.theme.registerVariant("-outline", com.exura.components.buttons.ButtonOutlineVariant
        )
        ctx.theme.registerVariant("-ghost", com.exura.components.buttons.ButtonGhostVariant
        )
        ctx.theme.registerVariant("-danger", com.exura.components.buttons.ButtonDangerVariant
        )
        ctx.theme.registerVariant("-disabled", com.exura.components.buttons.ButtonDisabledVariant
        )
        ctx.theme.registerVariant("-large", com.exura.components.dialogs.DialogPanelLargeVariant
        )
        ctx.theme.registerVariant("-ghost", com.exura.components.inputs.InlineInputGhostVariant
        )
        ctx.theme.registerVariant("-compact", com.exura.components.inputs.InlineInputCompactVariant
        )
        ctx.theme.registerVariant("-disabled", com.exura.components.inputs.InlineInputDisabledVariant
        )
        ctx.theme.registerVariant("-read-only", com.exura.components.inputs.InlineInputReadOnlyVariant
        )
        ctx.theme.registerVariant("-selected", com.exura.components.inputs.ModeToggleOptionSelectedVariant
        )
        ctx.theme.registerVariant("-collapsed", com.exura.components.layouts.nav.NavTextCollapsedVariant
        )
        ctx.theme.registerVariant("-collapsed-visible", com.exura.components.layouts.nav.NavTextCollapsedVisibleVariant
        )
        ctx.theme.registerVariant("-warn", com.exura.components.layouts.nav.NavNavigableElementWarnVariant
        )
        ctx.theme.registerVariant("-nav-style-collapsed", com.exura.components.layouts.nav.NavStyleCollapsed
        )
        ctx.theme.registerVariant("-bordered", com.exura.components.layouts.plan.PlanConstructorHeaderElementBorderedVariant
        )
        ctx.theme.registerVariant("-compact", com.exura.components.layouts.plan.SideBarCompactVariant
        )
        ctx.theme.registerVariant("-hoverable", com.exura.components.layouts.plan.SideBarRowHoverableVariant
        )
        ctx.theme.registerVariant("-separated", com.exura.components.layouts.plan.SideBarRowSeparatedVariant
        )
        ctx.theme.registerVariant("-separated-thick", com.exura.components.layouts.plan.SideBarRowSeparatedThickVariant
        )
        ctx.theme.registerVariant("-selected", com.exura.components.layouts.plan.SideBarRowSelectedVariant
        )
        ctx.theme.registerVariant("-teal", com.exura.components.layouts.plan.SideBarRowTealVariant
        )
        ctx.theme.registerVariant("-red", com.exura.components.layouts.plan.SideBarRowRedVariant
        )
        ctx.theme.registerVariant("-blue", com.exura.components.layouts.plan.SideBarRowBlueVariant
        )
        ctx.theme.registerVariant("-orange", com.exura.components.layouts.plan.SideBarRowOrangeVariant
        )
        ctx.theme.registerVariant("-list", com.exura.components.layouts.plan.SideBarRowContentListVariant
        )
        ctx.theme.registerVariant("-nested", com.exura.components.layouts.plan.SideBarRowContentNestedVariant
        )
        ctx.theme.registerVariant("-indent", com.exura.components.layouts.plan.SideBarRowContentIndentVariant
        )
        ctx.theme.registerVariant("-gantt", com.exura.components.layouts.plan.SideBarRowContentGanttVariant
        )
        ctx.theme.registerVariant("-hidden", com.exura.components.layouts.plan.SideBarRowContentHiddenVariant
        )
        ctx.theme.registerVariant("-collection-height", com.exura.components.layouts.plan.SideBarRowContentCollectionHeightVariant
        )
        ctx.theme.registerVariant("-series-height", com.exura.components.layouts.plan.SideBarRowContentSeriesHeightVariant
        )
        ctx.theme.registerVariant("-header-height", com.exura.components.layouts.plan.SideBarRowContentHeaderHeightVariant
        )
        ctx.theme.registerVariant("-starting", com.exura.components.layouts.plan.GantDayBoxStartingVariant
        )
        ctx.theme.registerVariant("-ending", com.exura.components.layouts.plan.GantDayBoxEndingVariant
        )
        ctx.theme.registerVariant("-plan-anchor-start", com.exura.components.layouts.plan.GantDayBoxPlanAnchorStartVariant
        )
        ctx.theme.registerVariant("-plan-anchor-end", com.exura.components.layouts.plan.GantDayBoxPlanAnchorEndVariant
        )
        ctx.theme.registerVariant("-middle", com.exura.components.layouts.plan.PlanConstructorSideBarDayBoxMiddleVariant
        )
        ctx.theme.registerVariant("-start", com.exura.components.layouts.plan.PlanConstructorSideBarDayBoxStartVariant
        )
        ctx.theme.registerVariant("-end", com.exura.components.layouts.plan.PlanConstructorSideBarDayBoxEndVariant
        )
        ctx.theme.registerVariant("-single-day", com.exura.components.layouts.plan.PlanConstructorSideBarDayBoxSingleDayVariant
        )
        ctx.theme.registerVariant("-hoverable", com.exura.components.layouts.plan.PlanConstructorSideBarDayBoxHoverableVariant
        )
        ctx.theme.registerVariant("-filled", com.exura.components.layouts.plan.PlanConstructorSideBarDayBoxFilledVariant
        )
        ctx.theme.registerVariant("-filled", com.exura.components.layouts.plan.DayBoxSeriesElementFilledVariant
        )
        ctx.theme.registerVariant("-dimmed", com.exura.components.loading.LoaderOverlayDimmedVariant
        )
        ctx.theme.registerVariant("-inline", com.exura.components.loading.InlineLoaderVariant
        )
        ctx.theme.registerVariant("-log-in", com.exura.components.loading.LogInLoaderVariant
        )
        ctx.theme.registerVariant("-ghost", com.exura.components.pageTypography.GhostCardEntryVariant
        )
        ctx.theme.registerVariant("-disabled", com.exura.components.pageTypography.DisabledCardEntryVariant
        )
        ctx.theme.registerVariant("-highlighted", com.exura.components.pageTypography.HighlightedCardEntryVariant
        )
        ctx.theme.registerVariant("-interactive", com.exura.components.pageTypography.InteractiveCardEntryVariant
        )
        ctx.theme.registerVariant("-hoverable", com.exura.components.pageTypography.HoverableListEntryVariant
        )
        ctx.theme.registerVariant("-hoverable-list-entry-surface", com.exura.components.pageTypography.HoverableListEntrySurfaceVariant
        )
        ctx.theme.registerVariant("-selected", com.exura.components.pageTypography.SelectedListEntryVariant
        )
        ctx.theme.registerVariant("-large", com.exura.components.pageTypography.ListEntryImageLargeVariant
        )
        ctx.theme.registerVariant("-no-image", com.exura.components.pageTypography.ListEntryImageNoImageVariant
        )
        ctx.theme.registerVariant("-page-content-no-indent", com.exura.components.pageTypography.PageContentNoIndentVariant
        )
        ctx.theme.registerVariant("-exura-poppins", com.exura.components.pageTypography.ExuraPoppinsVariant
        )
        ctx.theme.registerVariant("-exura-base-weight", com.exura.components.pageTypography.ExuraBaseWeightVariant
        )
        ctx.theme.registerVariant("-exura-emphasised", com.exura.components.pageTypography.ExuraEmphasisedTextVariant
        )
        ctx.theme.registerVariant("-exura-commentary", com.exura.components.pageTypography.ExuraCommentaryTextVariant
        )
        ctx.theme.registerVariant("-exura-underwrite", com.exura.components.pageTypography.ExuraUnderwriteTextVariant
        )
        ctx.theme.registerVariant("-exura-body-size", com.exura.components.pageTypography.ExuraBodySizeTextVariant
        )
        ctx.theme.registerVariant("-exura-body-header", com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
        )
        ctx.theme.registerVariant("-exura-hero", com.exura.components.pageTypography.ExuraHeroTextVariant
        )
        ctx.theme.registerVariant("-exura-section-header", com.exura.components.pageTypography.ExuraSectionHeaderTextVariant
        )
        ctx.theme.registerVariant("-exura-page-header", com.exura.components.pageTypography.ExuraPageHeaderTextVariant
        )
        ctx.theme.registerVariant("-exura-base-color", com.exura.components.pageTypography.ExuraBaseColorVariant
        )
        ctx.theme.registerVariant("-exura-primary-control", com.exura.components.pageTypography.ExuraPrimaryControlTextVariant
        )
        ctx.theme.registerVariant("-exura-success", com.exura.components.pageTypography.ExuraSuccessTextVariant
        )
        ctx.theme.registerVariant("-exura-warn", com.exura.components.pageTypography.ExuraWarnTextVariant
        )
        ctx.theme.registerVariant("-exura-error", com.exura.components.pageTypography.ExuraErrorTextVariant
        )
        ctx.theme.registerVariant("-exura-secondary", com.exura.components.pageTypography.ExuraSecondaryTextVariant
        )
        ctx.theme.registerVariant("-exura-link", com.exura.components.pageTypography.ExuraLinkTextVariant
        )
        ctx.theme.registerKeyframes("double-heartbeat-spin", com.exura.components.loading.DoubleHeartbeatSpin)
        com.exura.setInitialColorMode(ctx)
        com.exura.updateTheme(ctx)
    }

    router.tryRoutingTo(BasePath.remove(window.location.href.removePrefix(window.origin)), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.exura.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
